// ============================================
// Digital Signs - Admin Pairing Page
// Admin enters PIN to approve display pairing
// Shows device fingerprint info for verification
// URL: /pair
// ============================================

import React, { useState, useEffect, useRef } from 'react';

const API_BASE = '/api/trpc';

// API helper
async function apiCall(procedure, input = {}) {
  const res = await fetch(`${API_BASE}/${procedure}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    credentials: 'include',
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'API Error');
  return data.result?.data;
}

// Icons
const Monitor = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 64, height: 64 }}><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>;
const Check = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 64, height: 64 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
const AlertCircle = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 32, height: 32 }}><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>;
const Loader = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 24, height: 24 }} className="spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;
const Shield = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const Fingerprint = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}><path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4"/><path d="M5 19.5C5.5 18 6 15 6 12c0-.7.12-1.37.34-2"/><path d="M17.29 21.02c.12-.6.43-2.3.5-3.02"/><path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 4"/><path d="M8.65 22c.21-.66.45-1.32.57-2"/><path d="M14 13.12c0 2.38 0 6.38-1 8.88"/><path d="M2 16h.01"/><path d="M21.8 16c.2-2 .131-5.354 0-6"/><path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2"/></svg>;
const Globe = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>;
const Cpu = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/></svg>;
const MonitorIcon = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>;

// Styles
const styles = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.pairing-container {
  width: 100%;
  max-width: 520px;
  padding: 20px;
  animation: slideUp 0.5s ease-out;
}

.pairing-card {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 40px;
  text-align: center;
}

.logo-container {
  margin-bottom: 28px;
}

.logo-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 88px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border-radius: 20px;
  margin-bottom: 16px;
  color: white;
}

.logo-text {
  font-size: 24px;
  font-weight: 800;
  background: linear-gradient(135deg, #60a5fa, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.pairing-title {
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 8px;
}

.pairing-subtitle {
  font-size: 15px;
  color: #94a3b8;
  margin-bottom: 28px;
}

.pin-input-container {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 28px;
}

.pin-digit {
  width: 56px;
  height: 72px;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  font-size: 28px;
  font-weight: 700;
  color: white;
  text-align: center;
  outline: none;
  transition: all 0.2s;
}

.pin-digit:focus {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
}

.pin-digit::placeholder {
  color: #475569;
}

.lookup-btn {
  width: 100%;
  padding: 16px 28px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.lookup-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 40px rgba(59, 130, 246, 0.3);
}

.lookup-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 20px;
  color: #fca5a5;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
  font-size: 14px;
}

/* Device Info Card */
.device-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: left;
}

.device-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.device-icon {
  width: 48px;
  height: 48px;
  background: rgba(59, 130, 246, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #60a5fa;
}

.device-title {
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.device-subtitle {
  font-size: 13px;
  color: #64748b;
}

.device-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.device-item {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 10px;
  padding: 12px;
}

.device-item.full-width {
  grid-column: span 2;
}

.device-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.device-value {
  font-size: 14px;
  color: #e2e8f0;
  font-weight: 500;
  word-break: break-all;
}

/* Form inputs */
.form-group {
  margin-bottom: 16px;
  text-align: left;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 6px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: white;
  font-size: 15px;
  outline: none;
  transition: all 0.2s;
}

.form-input:focus {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.05);
}

.form-input::placeholder {
  color: #475569;
}

/* Checkbox */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 10px;
  margin-bottom: 20px;
  cursor: pointer;
}

.checkbox-group input {
  width: 18px;
  height: 18px;
  accent-color: #22c55e;
}

.checkbox-label {
  font-size: 14px;
  color: #86efac;
  display: flex;
  align-items: center;
  gap: 6px;
}

.checkbox-hint {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

/* Buttons */
.btn-group {
  display: flex;
  gap: 12px;
}

.approve-btn {
  flex: 1;
  padding: 14px 24px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.approve-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(34, 197, 94, 0.3);
}

.approve-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 14px 24px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* Success state */
.success-container {
  animation: slideUp 0.5s ease-out;
}

.success-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  border-radius: 50%;
  margin-bottom: 20px;
  color: white;
}

.success-title {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #22c55e;
}

.success-message {
  font-size: 16px;
  color: #94a3b8;
}

.footer-text {
  margin-top: 28px;
  font-size: 13px;
  color: #475569;
}

.footer-text a {
  color: #3b82f6;
  text-decoration: none;
}

.footer-text a:hover {
  text-decoration: underline;
}

/* Back link */
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #64748b;
  font-size: 14px;
  text-decoration: none;
  margin-bottom: 20px;
  transition: color 0.2s;
}

.back-link:hover {
  color: #94a3b8;
}
`;

export default function PairingPage() {
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('enter'); // enter, confirm, success
  const [displayInfo, setDisplayInfo] = useState(null);
  const [nickname, setNickname] = useState('');
  const [lockFingerprint, setLockFingerprint] = useState(true);
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    if (e.key === 'Enter' && !pin.includes('')) {
      handleLookup();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newPin = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
      setPin(newPin);
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleLookup = async () => {
    const fullPin = pin.join('');
    if (fullPin.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Look up display by PIN
      const result = await apiCall('displays.getByPin', { pinCode: fullPin });
      
      if (!result) {
        throw new Error('No display found with this PIN');
      }

      setDisplayInfo(result);
      setNickname(result.nickname || '');
      setStep('confirm');

    } catch (err) {
      setError(err.message || 'Invalid PIN code. Please try again.');
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setLoading(true);
    setError('');

    try {
      await apiCall('displays.approvePairing', {
        pinCode: pin.join(''),
        nickname: nickname || undefined,
        lockFingerprint: lockFingerprint,
      });

      setStep('success');

    } catch (err) {
      setError(err.message || 'Failed to approve pairing');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setStep('enter');
    setDisplayInfo(null);
    setPin(['', '', '', '', '', '']);
    setError('');
    inputRefs.current[0]?.focus();
  };

  // Render success state
  if (step === 'success') {
    return (
      <>
        <style>{styles}</style>
        <div className="pairing-container">
          <div className="pairing-card success-container">
            <div className="success-icon">
              <Check />
            </div>
            <h1 className="success-title">Display Paired!</h1>
            <p className="success-message">
              {nickname || 'The display'} is now connected and will start showing content.
            </p>
            <div className="footer-text">
              <a href="/">← Return to Dashboard</a>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Render confirmation step with device info
  if (step === 'confirm' && displayInfo) {
    const deviceInfo = displayInfo.deviceInfo || {};
    
    return (
      <>
        <style>{styles}</style>
        <div className="pairing-container">
          <a href="#" className="back-link" onClick={(e) => { e.preventDefault(); handleCancel(); }}>
            ← Back to PIN entry
          </a>
          
          <div className="pairing-card">
            <h1 className="pairing-title">Confirm Display</h1>
            <p className="pairing-subtitle">
              Review the device information before approving
            </p>

            {error && (
              <div className="error-message">
                <AlertCircle />
                <span>{error}</span>
              </div>
            )}

            <div className="device-card">
              <div className="device-header">
                <div className="device-icon">
                  <Monitor style={{ width: 24, height: 24 }} />
                </div>
                <div>
                  <div className="device-title">PIN: {pin.join('')}</div>
                  <div className="device-subtitle">
                    {displayInfo.status === 'pending' ? 'Pending Approval' : displayInfo.status}
                  </div>
                </div>
              </div>

              <div className="device-grid">
                <div className="device-item">
                  <div className="device-label"><Cpu /> System</div>
                  <div className="device-value">
                    {deviceInfo.osName || displayInfo.osName || 'Unknown'} {deviceInfo.osVersion || displayInfo.osVersion || ''}
                  </div>
                </div>
                <div className="device-item">
                  <div className="device-label"><Globe /> Browser</div>
                  <div className="device-value">
                    {deviceInfo.browserName || displayInfo.browserType || 'Unknown'} {deviceInfo.browserVersion || ''}
                  </div>
                </div>
                <div className="device-item">
                  <div className="device-label"><MonitorIcon /> Screen</div>
                  <div className="device-value">
                    {deviceInfo.screenResolution || displayInfo.screenResolution || 'Unknown'}
                  </div>
                </div>
                <div className="device-item">
                  <div className="device-label">Timezone</div>
                  <div className="device-value">
                    {deviceInfo.timezone || displayInfo.timezone || 'Unknown'}
                  </div>
                </div>
                <div className="device-item">
                  <div className="device-label">IP Address</div>
                  <div className="device-value">
                    {displayInfo.publicIp || displayInfo.lastIpAddress || 'Unknown'}
                  </div>
                </div>
                <div className="device-item">
                  <div className="device-label">GPU</div>
                  <div className="device-value" style={{ fontSize: 12 }}>
                    {deviceInfo.webglRenderer || displayInfo.webglRenderer || 'Unknown'}
                  </div>
                </div>
                {displayInfo.fingerprint && (
                  <div className="device-item full-width">
                    <div className="device-label"><Fingerprint /> Fingerprint</div>
                    <div className="device-value" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {displayInfo.fingerprint}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Display Name (Optional)</label>
              <input
                type="text"
                className="form-input"
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                placeholder="e.g., Lobby TV, Front Counter"
              />
            </div>

            <label className="checkbox-group">
              <input
                type="checkbox"
                checked={lockFingerprint}
                onChange={e => setLockFingerprint(e.target.checked)}
              />
              <div>
                <div className="checkbox-label">
                  <Shield /> Lock device fingerprint
                </div>
                <div className="checkbox-hint">
                  Require approval if device hardware changes
                </div>
              </div>
            </label>

            <div className="btn-group">
              <button className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button 
                className="approve-btn" 
                onClick={handleApprove}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader />
                    Approving...
                  </>
                ) : (
                  <>
                    <Check style={{ width: 20, height: 20 }} />
                    Approve & Connect
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Render PIN entry step
  return (
    <>
      <style>{styles}</style>
      <div className="pairing-container">
        <div className="pairing-card">
          <div className="logo-container">
            <div className="logo-icon">
              <Monitor />
            </div>
            <div className="logo-text">Digital Signs</div>
          </div>

          <h1 className="pairing-title">Pair a Display</h1>
          <p className="pairing-subtitle">
            Enter the 6-digit PIN shown on the display screen
          </p>

          {error && (
            <div className="error-message">
              <AlertCircle />
              <span>{error}</span>
            </div>
          )}

          <div className="pin-input-container" onPaste={handlePaste}>
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="pin-digit"
                value={digit}
                onChange={e => handleDigitChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                placeholder="•"
                disabled={loading}
                autoComplete="off"
              />
            ))}
          </div>

          <button 
            className="lookup-btn" 
            onClick={handleLookup}
            disabled={loading || pin.includes('')}
          >
            {loading ? (
              <>
                <Loader />
                Looking up...
              </>
            ) : (
              'Find Display'
            )}
          </button>

          <p className="footer-text">
            Point your display to: <strong>{window.location.origin}/display</strong>
          </p>
        </div>
      </div>
    </>
  );
}
